import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, AlertCircle, Upload, Camera, Keyboard, Loader2, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { formatApiErrorDetail } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SERVICE = { service_type: '', service_amount: '', physical_care: '', period_start: '', period_end: '' };

const INITIAL_FORM_DATA = {
  recipient_number: '', address: '', name_kana: '', name: '', birth_date: '',
  child_name_kana: '', child_name: '', child_birth_date: '',
  disability_type: '', issue_date: '', municipality: '',
  services: [{ ...EMPTY_SERVICE }, { ...EMPTY_SERVICE }, { ...EMPTY_SERVICE }],
  notes: '',
  burden_ratio: '', max_monthly_amount: 0,
  application_period_start: '', application_period_end: '',
  meal_provision_eligible: '', burden_limit_management: '',
  burden_limit_facility: '', special_notes: '', reserve_notes: '',
  facility_id: '',
};

export default function ClientFormPage() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const isEditMode = Boolean(clientId);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [inputMode, setInputMode] = useState('manual');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({ ...INITIAL_FORM_DATA });

  useEffect(() => { fetchFacilities(); }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchClient();
    } else {
      // 新規モード: フォームを完全リセット（facility_idは保持）
      setFormData(prev => ({
        ...INITIAL_FORM_DATA,
        services: [{ ...EMPTY_SERVICE }, { ...EMPTY_SERVICE }, { ...EMPTY_SERVICE }],
        facility_id: prev.facility_id || '',
      }));
      setError('');
      setOcrResult(null);
      setPreviewUrl(null);
      setInitialLoading(false);
    }
  }, [clientId]);

  const fetchClient = async () => {
    try {
      setInitialLoading(true);
      const res = await axios.get(`${API}/clients/${clientId}`);
      const c = res.data;
      const svcs = c.services || [];
      const paddedServices = [0, 1, 2].map(i =>
        svcs[i] ? { ...EMPTY_SERVICE, ...svcs[i] } : { ...EMPTY_SERVICE }
      );
      setFormData({
        recipient_number: c.recipient_number || '',
        address: c.address || '',
        name_kana: c.name_kana || '',
        name: c.name || '',
        birth_date: c.birth_date || '',
        child_name_kana: c.child_name_kana || '',
        child_name: c.child_name || '',
        child_birth_date: c.child_birth_date || '',
        disability_type: c.disability_type || '',
        issue_date: c.issue_date || '',
        municipality: c.municipality || '',
        services: paddedServices,
        notes: c.notes || '',
        burden_ratio: c.burden_ratio || '',
        max_monthly_amount: c.max_monthly_amount || 0,
        application_period_start: c.application_period_start || '',
        application_period_end: c.application_period_end || '',
        meal_provision_eligible: c.meal_provision_eligible || '',
        burden_limit_management: c.burden_limit_management || '',
        burden_limit_facility: c.burden_limit_facility || '',
        special_notes: c.special_notes || '',
        reserve_notes: c.reserve_notes || '',
        facility_id: c.facility_id || '',
      });
    } catch (err) {
      setError('利用者データの取得に失敗しました');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const res = await axios.get(`${API}/facilities`);
      setFacilities(res.data);
      if (res.data.length > 0) {
        setFormData(prev => {
          if (!prev.facility_id) {
            return { ...prev, facility_id: res.data[0].id };
          }
          return prev;
        });
      }
    } catch (e) { console.error('事業所データの取得に失敗', e); }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleServiceChange = (idx, field, value) => {
    setFormData(prev => {
      const svcs = [...prev.services];
      svcs[idx] = { ...svcs[idx], [field]: value };
      return { ...prev, services: svcs };
    });
  };

  const handleOcrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setError('ファイルサイズは20MB以下にしてください'); return; }
    setPreviewUrl(URL.createObjectURL(file));
    setOcrLoading(true); setError(''); setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/clients/ocr-certificate`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      const data = res.data;
      setOcrResult(data);
      const ext = data.extracted || {};
      // サービス枠をマージ（3枠固定）
      const ocrServices = ext.services || [];
      const mergedServices = [0, 1, 2].map(i =>
        ocrServices[i] ? { ...EMPTY_SERVICE, ...ocrServices[i] } : { ...EMPTY_SERVICE }
      );
      setFormData(prev => ({
        ...prev,
        recipient_number: ext.recipient_number || prev.recipient_number,
        address: ext.address || prev.address,
        name_kana: ext.name_kana || prev.name_kana,
        name: ext.name || prev.name,
        birth_date: ext.birth_date || prev.birth_date,
        child_name_kana: ext.child_name_kana || prev.child_name_kana,
        child_name: ext.child_name || prev.child_name,
        child_birth_date: ext.child_birth_date || prev.child_birth_date,
        disability_type: ext.disability_type || prev.disability_type,
        issue_date: ext.issue_date || prev.issue_date,
        municipality: ext.municipality || prev.municipality,
        services: ocrServices.length > 0 ? mergedServices : prev.services,
        notes: ext.notes || prev.notes,
        burden_ratio: ext.burden_ratio || prev.burden_ratio,
        max_monthly_amount: ext.max_monthly_amount ?? prev.max_monthly_amount,
        application_period_start: ext.application_period_start || prev.application_period_start,
        application_period_end: ext.application_period_end || prev.application_period_end,
        meal_provision_eligible: ext.meal_provision_eligible || prev.meal_provision_eligible,
        burden_limit_management: ext.burden_limit_management || prev.burden_limit_management,
        burden_limit_facility: ext.burden_limit_facility || prev.burden_limit_facility,
        special_notes: ext.special_notes || prev.special_notes,
        reserve_notes: ext.reserve_notes || prev.reserve_notes,
      }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'OCR読み取りに失敗しました');
    } finally { setOcrLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const payload = {
        ...formData,
        max_monthly_amount: parseInt(formData.max_monthly_amount) || 0,
        services: formData.services.filter(s => s.service_type),
        facility_id: formData.facility_id || (facilities.length > 0 ? facilities[0].id : ''),
      };
      if (isEditMode) {
        await axios.put(`${API}/clients/${clientId}`, payload);
        navigate(`/clients/${clientId}`);
      } else {
        await axios.post(`${API}/clients`, payload);
        navigate('/clients');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(isEditMode ? `/clients/${clientId}` : '/clients')} data-testid="back-button">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{isEditMode ? '利用者編集' : '利用者登録'}</h1>
          <p className="text-muted-foreground mt-1">{isEditMode ? '利用者情報を編集します' : '受給者証の情報を登録します'}</p>
        </div>
      </div>

      {/* 入力モード切替 */}
      <Card className="card-surface">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Button type="button" variant={inputMode === 'manual' ? 'default' : 'outline'}
              onClick={() => setInputMode('manual')} className={inputMode === 'manual' ? 'btn-active' : ''}
              data-testid="mode-manual-btn"><Keyboard size={18} className="mr-2" />手入力</Button>
            <Button type="button" variant={inputMode === 'ocr' ? 'default' : 'outline'}
              onClick={() => setInputMode('ocr')} className={inputMode === 'ocr' ? 'btn-active' : ''}
              data-testid="mode-ocr-btn"><Camera size={18} className="mr-2" />受給者証から自動入力</Button>
          </div>
        </CardContent>
      </Card>

      {/* OCRアップロード */}
      {inputMode === 'ocr' && (
        <Card className="card-surface border-2 border-dashed border-primary/30" data-testid="ocr-upload-card">
          <CardHeader><CardTitle className="flex items-center gap-2"><Upload size={22} />受給者証をアップロード</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">受給者証の写真またはPDFをアップロードすると、OCRで自動的に情報を読み取ります。</p>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleOcrUpload} data-testid="ocr-file-input" />
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={ocrLoading} className="min-w-[200px]" data-testid="ocr-upload-btn">
                {ocrLoading ? (<><Loader2 size={18} className="mr-2 animate-spin" />読み取り中...</>) : (<><Upload size={18} className="mr-2" />ファイルを選択</>)}
              </Button>
              {previewUrl && <div className="w-48 h-32 rounded-lg overflow-hidden border"><img src={previewUrl} alt="プレビュー" className="w-full h-full object-cover" /></div>}
            </div>
            {ocrResult && (
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="ocr-result-info">
                <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2"><CheckCircle size={20} />読み取り完了（{ocrResult.filled_fields}/{ocrResult.total_fields}項目）</div>
                <p className="text-sm text-emerald-600">{ocrResult.lines_detected}行検出 | エンジン: {ocrResult.ocr_engine} | 信頼度: {Math.round((ocrResult.confidence || 0) * 100)}%</p>
                <p className="text-xs text-muted-foreground mt-1">下のフォームに自動入力されました。内容を確認・修正してください。</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="flex items-center gap-2 p-4 rounded-lg bg-destructive/10 text-destructive" data-testid="form-error-message"><AlertCircle size={20} /><span>{error}</span></div>}

        {/* （一）受給者証番号・支給決定障害者等 */}
        <Card className="card-surface">
          <CardHeader><CardTitle>（一）受給者証番号・支給決定障害者等</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>受給者証番号</Label>
                <Input value={formData.recipient_number} onChange={e => handleChange('recipient_number', e.target.value)} placeholder="00000091825" data-testid="client-recipient-number" />
              </div>
              <div className="space-y-2">
                <Label>所属事業所</Label>
                <Select value={formData.facility_id} onValueChange={v => handleChange('facility_id', v)}>
                  <SelectTrigger data-testid="client-facility-select"><SelectValue placeholder="事業所を選択" /></SelectTrigger>
                  <SelectContent>{facilities.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* 支給決定障害者等 */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-muted-foreground mb-4">支給決定障害者等</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label>居住地</Label>
                  <Input value={formData.address} onChange={e => handleChange('address', e.target.value)} placeholder="三重県津市..." data-testid="client-address" />
                </div>
                <div className="space-y-2">
                  <Label>フリガナ</Label>
                  <Input value={formData.name_kana} onChange={e => handleChange('name_kana', e.target.value)} placeholder="ヤマダ タロウ" data-testid="client-name-kana" />
                </div>
                <div className="space-y-2">
                  <Label>氏名</Label>
                  <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="山田 太郎" data-testid="client-name" />
                </div>
                <div className="space-y-2">
                  <Label>生年月日</Label>
                  <Input type="date" value={formData.birth_date} onChange={e => handleChange('birth_date', e.target.value)} data-testid="client-birth-date" />
                </div>
              </div>
            </div>

            {/* 障害児 */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-muted-foreground mb-4">障害児</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>フリガナ</Label>
                  <Input value={formData.child_name_kana} onChange={e => handleChange('child_name_kana', e.target.value)} placeholder="" data-testid="client-child-name-kana" />
                </div>
                <div className="space-y-2">
                  <Label>氏名</Label>
                  <Input value={formData.child_name} onChange={e => handleChange('child_name', e.target.value)} placeholder="" data-testid="client-child-name" />
                </div>
                <div className="space-y-2">
                  <Label>生年月日</Label>
                  <Input type="date" value={formData.child_birth_date} onChange={e => handleChange('child_birth_date', e.target.value)} data-testid="client-child-birth-date" />
                </div>
              </div>
            </div>

            {/* 障害種別・交付年月日・支給市町村 */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>障害種別</Label>
                  <Input value={formData.disability_type} onChange={e => handleChange('disability_type', e.target.value)} placeholder="知的障害" data-testid="client-disability-type" />
                </div>
                <div className="space-y-2">
                  <Label>交付年月日</Label>
                  <Input type="date" value={formData.issue_date} onChange={e => handleChange('issue_date', e.target.value)} data-testid="client-issue-date" />
                </div>
                <div className="space-y-2">
                  <Label>支給市町村名</Label>
                  <Input value={formData.municipality} onChange={e => handleChange('municipality', e.target.value)} placeholder="津市" data-testid="client-municipality" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* （二）サービス（3枠） */}
        <Card className="card-surface">
          <CardHeader><CardTitle>（二）サービス内容</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            {formData.services.map((svc, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${svc.service_type ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">サービス {idx + 1}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>サービス種別</Label>
                    <Input value={svc.service_type} onChange={e => handleServiceChange(idx, 'service_type', e.target.value)} placeholder="移動支援" data-testid={`service-type-${idx}`} />
                  </div>
                  <div className="space-y-2">
                    <Label>支給量等</Label>
                    <Input value={svc.service_amount} onChange={e => handleServiceChange(idx, 'service_amount', e.target.value)} placeholder="10時間/月" data-testid={`service-amount-${idx}`} />
                  </div>
                  <div className="space-y-2">
                    <Label>身体介護有無</Label>
                    <Select value={svc.physical_care || '_none'} onValueChange={v => handleServiceChange(idx, 'physical_care', v === '_none' ? '' : v)}>
                      <SelectTrigger data-testid={`service-physical-care-${idx}`}><SelectValue placeholder="選択" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">-</SelectItem>
                        <SelectItem value="身体介護を伴う">身体介護を伴う</SelectItem>
                        <SelectItem value="身体介護を伴わない">身体介護を伴わない</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <div></div>
                  </div>
                  <div className="space-y-2">
                    <Label>支給決定期間（開始）</Label>
                    <Input type="date" value={svc.period_start} onChange={e => handleServiceChange(idx, 'period_start', e.target.value)} data-testid={`service-period-start-${idx}`} />
                  </div>
                  <div className="space-y-2">
                    <Label>支給決定期間（終了）</Label>
                    <Input type="date" value={svc.period_end} onChange={e => handleServiceChange(idx, 'period_end', e.target.value)} data-testid={`service-period-end-${idx}`} />
                  </div>
                </div>
              </div>
            ))}

            <div className="space-y-2">
              <Label>備考欄</Label>
              <Textarea value={formData.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="備考" rows={3} data-testid="client-notes" />
            </div>
          </CardContent>
        </Card>

        {/* （三）利用者負担に関する事項 */}
        <Card className="card-surface">
          <CardHeader><CardTitle>（三）利用者負担に関する事項</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>利用者負担割合</Label>
              <Select value={formData.burden_ratio} onValueChange={v => handleChange('burden_ratio', v)}>
                <SelectTrigger data-testid="client-burden-ratio"><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0割">0割</SelectItem>
                  <SelectItem value="1割">1割</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>利用者負担上限月額</Label>
              <Input type="number" value={formData.max_monthly_amount} onChange={e => handleChange('max_monthly_amount', e.target.value)} placeholder="0" data-testid="client-max-amount" />
            </div>
            <div className="space-y-2">
              <Label>適用期間（開始）</Label>
              <Input type="date" value={formData.application_period_start} onChange={e => handleChange('application_period_start', e.target.value)} data-testid="client-app-period-start" />
            </div>
            <div className="space-y-2">
              <Label>適用期間（終了）</Label>
              <Input type="date" value={formData.application_period_end} onChange={e => handleChange('application_period_end', e.target.value)} data-testid="client-app-period-end" />
            </div>
            <div className="space-y-2">
              <Label>食事提供体制加算対象者</Label>
              <Select value={formData.meal_provision_eligible} onValueChange={v => handleChange('meal_provision_eligible', v)}>
                <SelectTrigger data-testid="client-meal"><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent><SelectItem value="該当">該当</SelectItem><SelectItem value="非該当">非該当</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>利用者上限額管理対象者該当の有無</Label>
              <Select value={formData.burden_limit_management} onValueChange={v => handleChange('burden_limit_management', v)}>
                <SelectTrigger data-testid="client-burden-mgmt"><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent><SelectItem value="該当">該当</SelectItem><SelectItem value="非該当">非該当</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>利用者負担額上限額管理事業所名</Label>
              <Input value={formData.burden_limit_facility} onChange={e => handleChange('burden_limit_facility', e.target.value)} placeholder="" data-testid="client-burden-facility" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>特記事項欄</Label>
              <Textarea value={formData.special_notes} onChange={e => handleChange('special_notes', e.target.value)} placeholder="" rows={3} data-testid="client-special-notes" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>予備欄</Label>
              <Textarea value={formData.reserve_notes} onChange={e => handleChange('reserve_notes', e.target.value)} placeholder="" rows={2} data-testid="client-reserve-notes" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(isEditMode ? `/clients/${clientId}` : '/clients')} data-testid="cancel-button">キャンセル</Button>
          <Button type="submit" disabled={loading} className="btn-active" data-testid="submit-button">
            <Save size={18} className="mr-2" />{loading ? '保存中...' : isEditMode ? '更新する' : '保存する'}
          </Button>
        </div>
      </form>
    </div>
  );
}
